<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import CsatForm from './components/CsatForm.vue';
import PlsDialog from '@/components/PlsDialog.vue';
import type { PlsDialogAction } from '@/components/PlsDialog.types';
import { PleasedApi } from '@/pleased-sdk';
import { ChatRateMode } from '@/common/api/models.pleased';
import { useOverlayCustomisation } from '@/views/ChatWidget/composables/useOverlayCustomisation';
import { createBackgroundStyle } from '@/common/customisation/utils/styleUtils';
import { analytics, COMPONENT_EVENTS } from '@/common/logs';
import { debounce } from '@/vendor/pleased-sdk/util';
import { useI18n } from '@/plugins/i18n';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { storeToRefs } from 'pinia';
import { CsatFormData } from './types/csat.types';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

const MIN_RATING_FOR_SUPPORT = 2;
const MIN_RATING_FOR_FEEDBACK_SELECTIONS = 3;

export interface FeedbackPageProps {
  entityId?: string;
  entityType?: ChatRateMode;
}

const { t } = useI18n();

const props = defineProps<FeedbackPageProps>();

const emit = defineEmits<{
  (event: 'submit'): void;
  (event: 'skip'): void;
  (event: 'low-rating-support'): void;
}>();

const submitted = ref(false);
const showConnectSupportDialog = ref(false);
const hasShownLowRatingDialog = ref(false);

const connectConfigStore = useConnectConfig();
const { connectConfig, canConnectToAgent } = storeToRefs(connectConfigStore);

const overlayCustomisation = useOverlayCustomisation();
const containerStyle = computed(() => createBackgroundStyle(overlayCustomisation.backgroundColor));

/**
 * Opens the connect support modal after low rating submission
 */
const openConnectSupportModal = () => {
  if (!canConnectToAgent.value) return;

  analytics.track({
    name: `${COMPONENT_EVENTS.CSAT_LOW_RATING_SHOWN}`,
    selection: props.entityId,
    metadata: { type: props.entityType },
  });

  showConnectSupportDialog.value = true;
};

/**
 * Handles "Yes" click - connect to support
 */
const handleConnectSupport = () => {
  analytics.trackClick({
    name: `${COMPONENT_EVENTS.CSAT_LOW_RATING_CONNECT_SUPPORT}`,
    selection: props.entityId,
    metadata: { type: props.entityType },
  });

  showConnectSupportDialog.value = false;
  // Low rating support flow is disabled for now
  // emit('low-rating-support');
};

/**
 * Handles "No" click - close without connecting
 */
const handleDeclineSupport = async () => {
  analytics.trackClick({
    name: `${COMPONENT_EVENTS.CSAT_LOW_RATING_CLOSE}`,
    selection: props.entityId,
    metadata: { type: props.entityType },
  });

  showConnectSupportDialog.value = false;
  // emit('submit');
};

const connectSupportDialogActions = computed<PlsDialogAction[]>(() => {
  return [
    {
      id: 'no',
      text: String(t('global.no')),
      color: 'secondary',
      action: handleDeclineSupport,
    },
    {
      id: 'yes',
      text: String(t('global.yes')),
      color: 'primary',
      action: handleConnectSupport,
    },
  ];
});

const connectSupportDialogContent = computed(() => {
  return String(t('page.feedback.connect_support_dialog.text'));
});

const SUBMIT_DEBOUNCE_MS = 500;

/**
 * Sends CSAT feedback to the API when payload can be built (entity + rating).
 */
const postCsatFeedbackIfEligible = async (data: CsatFormData): Promise<void> => {
  if (data.rating === null) return;

  if (!props.entityType || !props.entityId) return;

  const payload = {
    type: props.entityType,
    chatOrVoiceId: props.entityId,
    rating: data.rating,
    comment: data.comment,
    feedbackSelectionIds:
      (data.rating || 0) <= MIN_RATING_FOR_FEEDBACK_SELECTIONS ? data.selectedImprovements.map(Number) : [],
  };

  try {
    await PleasedApi.postFeedback(payload);
  } catch (error: unknown) {
    captureEventException(error, ErrorTrackingEvent.CsatSubmit);
    throw error;
  }
};

const debouncedPostCsatFeedbackIfEligible = debounce(async (data: CsatFormData) => {
  try {
    await postCsatFeedbackIfEligible(data);
  } catch {
    // Already reported via CsatSubmit; debounce returns void so swallow here.
  }
}, SUBMIT_DEBOUNCE_MS);

const submitFeedback = async (data: CsatFormData) => {
  if (submitted.value) return;
  if (data.rating === null || !props.entityType || !props.entityId) return;

  try {
    await postCsatFeedbackIfEligible(data);
  } catch {
    // Already reported to GlitchTip; keep the form open so the visitor can retry.
    return;
  }

  analytics.trackClick({
    name: COMPONENT_EVENTS.CSAT_SUBMITTED,
    selection: props.entityId,
    metadata: { type: props.entityType },
  });

  submitted.value = true;

  emit('submit');
};

const handleSubmit = (data: CsatFormData) => {
  void submitFeedback(data);
};

const handleSkip = () => {
  analytics.trackClick({
    name: COMPONENT_EVENTS.CSAT_SKIP,
    selection: props.entityId,
    metadata: { type: props.entityType },
  });

  emit('skip');
};

const handleRatingSelected = (data: CsatFormData) => {
  analytics.trackClick({
    name: COMPONENT_EVENTS.CSAT_CLICKED,
    selection: props.entityId,
    metadata: { rating: data.rating, type: props.entityType, UI: 'new' },
  });

  debouncedPostCsatFeedbackIfEligible(data);
  // Low rating support flow is disabled for now
  // const shouldShowConnectSupportModal =
  //   rating <= MIN_RATING_FOR_SUPPORT &&
  //   props.entityType === 'AI' &&
  //   canConnectToAgent.value &&
  //   !hasShownLowRatingDialog.value;

  // if (shouldShowConnectSupportModal) {
  //   hasShownLowRatingDialog.value = true;
  //   openConnectSupportModal();
  // }
};

onMounted(() => {
  // trackEntitySeen only supports numeric chat/voice/ticket ids. AI and voiceAi use a string
  // sessionId as entityId, so we exclude them here (otherwise parseInt would yield NaN).
  if (props.entityType === 'AI' || props.entityType === 'voiceAi' || !props.entityId || !props.entityType) return;
  PleasedApi.trackEntitySeen({ entityType: props.entityType, entityId: parseInt(props.entityId) });
});
</script>

<template>
  <div class="new-csat-view" data-testid="csat-view" :style="containerStyle">
    <CsatForm
      :entity-type="entityType"
      @rating-selected="handleRatingSelected"
      @submit="handleSubmit"
      @skip="handleSkip"
    />

    <PlsDialog
      :open="showConnectSupportDialog"
      :content="connectSupportDialogContent"
      :actions="connectSupportDialogActions"
    />
  </div>
</template>

<style scoped lang="scss">
.new-csat-view {
  display: flex;
  align-items: flex-start;
  justify-content: center;

  max-height: 75vh;
  overflow-y: auto;

  padding: 0 16px 24px 16px;
}
</style>
